/**
 * Tenant Store
 * Manages multi-tenant functionality and tenant-specific settings
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '@/services/api'
import type { Tenant } from '@/types'
import { apiErrorFromResponse, getApiErrorMessage } from '@/utils/apiErrorMessage'

export const useTenantStore = defineStore('tenant', () => {
  // ================================
  // State
  // ================================

  const currentTenant = ref<Tenant | null>(null)
  const availableTenants = ref<Tenant[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // ================================
  // Getters
  // ================================

  const tenantId = computed(() => currentTenant.value?.id || null)

  const tenantSlug = computed(() => currentTenant.value?.slug || null)

  const tenantName = computed(() => currentTenant.value?.name || null)

  const tenantSettings = computed(() => currentTenant.value?.settings || null)

  const currency = computed(() => currentTenant.value?.currency || 'DOP')

  const timezone = computed(() => currentTenant.value?.timezone || 'America/Santo_Domingo')

  const fiscalYearStart = computed(() => currentTenant.value?.fiscalYearStart || '01-01')

  const businessType = computed(() => currentTenant.value?.businessType || '')

  const businessArchetypes = computed(() => currentTenant.value?.businessArchetypes || [])

  const rnc = computed(() => currentTenant.value?.rnc || '')

  const dateFormat = computed(() => currentTenant.value?.settings?.dateFormat || 'DD/MM/YYYY')

  const numberFormat = computed(() => currentTenant.value?.settings?.numberFormat || 'es-DO')

  const itbisEnabled = computed(() => currentTenant.value?.settings?.taxSettings?.itbis?.enabled || false)

  const itbisRate = computed(() => 0.18)

  const hasTenant = computed(() => !!currentTenant.value)

  const hasFeature = (key: string): boolean => {
    const features = currentTenant.value?.features
    if (!features) return true
    if (Array.isArray(features)) return features.includes(key)
    if (!(key in features)) return true
    return Boolean((features as Record<string, boolean>)[key])
  }

  /**
   * Set of discrete module flags the tenant currently has enabled.
   * Used to gate navigation/routing (plan §3, D11). The server emits a
   * complete map with explicit `false` for hidden modules (D4/D9).
   */
  const MODULE_FLAGS = [
    'inventory', 'landedCosts', 'projects', 'overheadAllocation',
    'production', 'exportDocs', 'timeTracking', 'retainers'
  ] as const

  const enabledModules = computed<string[]>(() =>
    MODULE_FLAGS.filter((flag) => hasFeature(flag))
  )

  // ================================
  // Actions
  // ================================

  /**
   * Initialize tenant from route slug
   */
  const initializeFromSlug = async (slug: string) => {
    try {
      isLoading.value = true
      error.value = null

      // First check if we already have this tenant
      const existingTenant = availableTenants.value.find(t => t.slug === slug)
      if (existingTenant) {
        currentTenant.value = existingTenant
        return existingTenant
      }

      // Fetch tenant by slug from API
      const response = await apiService.get(`/tenants/by-slug/${slug}`)

      if (response.data.success) {
        const tenant = response.data.data
        currentTenant.value = tenant

        // Add to available tenants if not already there
        if (!availableTenants.value.find(t => t.id === tenant.id)) {
          availableTenants.value.push(tenant)
        }

        return tenant
      } else {
        throw apiErrorFromResponse(response, 'Empresa no encontrada')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al cargar la empresa')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Set current tenant
   */
  const setCurrentTenant = (tenant: Tenant) => {
    currentTenant.value = tenant

    // Add to available tenants if not already there
    if (!availableTenants.value.find(t => t.id === tenant.id)) {
      availableTenants.value.push(tenant)
    }
  }

  /**
   * Fetch user's available tenants
   */
  const fetchAvailableTenants = async () => {
    try {
      isLoading.value = true
      error.value = null

      const response = await apiService.get('/tenants/my-tenants')

      if (response.data.success) {
        availableTenants.value = response.data.data
        return availableTenants.value
      } else {
        throw apiErrorFromResponse(response, 'Error al cargar las empresas')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al cargar las empresas')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Switch to a different tenant
   */
  const switchTenant = async (tenantSlug: string) => {
    try {
      // Find tenant in available tenants
      const tenant = availableTenants.value.find(t => t.slug === tenantSlug)

      if (tenant) {
        currentTenant.value = tenant
        return tenant
      } else {
        // Fetch tenant if not in available list
        return await initializeFromSlug(tenantSlug)
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al cambiar de empresa')
      throw err
    }
  }

  /**
   * Update tenant settings
   */
  const updateTenantSettings = async (settings: Partial<Tenant['settings']>) => {
    try {
      isLoading.value = true
      error.value = null

      if (!currentTenant.value) {
        throw new Error('No active tenant')
      }

      const response = await apiService.put(`/tenants/${currentTenant.value.id}/settings`, {
        settings
      })

      if (response.data.success) {
        const updatedTenant = response.data.data
        currentTenant.value = updatedTenant

        // Update in available tenants as well
        const index = availableTenants.value.findIndex(t => t.id === updatedTenant.id)
        if (index !== -1) {
          availableTenants.value[index] = updatedTenant
        }

        return updatedTenant
      } else {
        throw apiErrorFromResponse(response, 'Error al actualizar la configuración')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al actualizar la configuración')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update tenant profile information
   */
  const updateTenantProfile = async (profileData: Partial<Tenant>) => {
    try {
      isLoading.value = true
      error.value = null

      if (!currentTenant.value) {
        throw new Error('No active tenant')
      }

      const response = await apiService.put(`/tenants/${currentTenant.value.id}`, profileData)

      if (response.data.success) {
        const updatedTenant = response.data.data
        currentTenant.value = updatedTenant

        // Update in available tenants as well
        const index = availableTenants.value.findIndex(t => t.id === updatedTenant.id)
        if (index !== -1) {
          availableTenants.value[index] = updatedTenant
        }

        return updatedTenant
      } else {
        throw apiErrorFromResponse(response, 'Error al actualizar el perfil')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al actualizar el perfil')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Format currency for display
   */
  const formatCurrency = (amount: number, includeCurrency = true): string => {
    const formatter = new Intl.NumberFormat(numberFormat.value, {
      style: includeCurrency ? 'currency' : 'decimal',
      currency: currency.value,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    return formatter.format(amount)
  }

  /**
   * Format date for display
   */
  const formatDate = (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date

    // Convert date format from DD/MM/YYYY to JavaScript format
    const jsFormat = dateFormat.value
      .replace('DD', 'dd')
      .replace('MM', 'MM')
      .replace('YYYY', 'yyyy')

    return new Intl.DateTimeFormat(numberFormat.value, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone.value
    }).format(dateObj)
  }

  /**
   * Format number for display
   */
  const formatNumber = (number: number, decimals = 2): string => {
    return new Intl.NumberFormat(numberFormat.value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number)
  }

  /**
   * Calculate ITBIS tax amount
   */
  const calculateItbis = (amount: number): number => {
    if (!itbisEnabled.value) return 0
    return amount * itbisRate.value
  }

  /**
   * Get next NCF number for a specific type
   */
  const getNextNCF = async (ncfType: string): Promise<string> => {
    try {
      if (!currentTenant.value) {
        throw new Error('No active tenant')
      }

      const { ncfApi } = await import('@/services/api')
      const response = await ncfApi.getNextNumber(ncfType)

      if (response.data.success) {
        return response.data.data?.ncf || response.data.data?.nextNcf || response.data.data?.number || ''
      }
      throw apiErrorFromResponse(response, 'Error al obtener el próximo NCF')
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al obtener el próximo NCF')
      throw err
    }
  }

  /**
   * Clear current tenant (logout)
   */
  const clearTenant = () => {
    currentTenant.value = null
    availableTenants.value = []
    error.value = null
  }

  /**
   * Clear error state
   */
  const clearError = () => {
    error.value = null
  }

  // ================================
  // Return store interface
  // ================================

  return {
    // State
    currentTenant: computed(() => currentTenant.value),
    availableTenants: computed(() => availableTenants.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // Getters
    tenantId,
    tenantSlug,
    tenantName,
    tenantSettings,
    currency,
    timezone,
    fiscalYearStart,
    businessType,
    businessArchetypes,
    rnc,
    dateFormat,
    numberFormat,
    itbisEnabled,
    itbisRate,
    hasTenant,
    hasFeature,
    enabledModules,

    // Actions
    initializeFromSlug,
    setCurrentTenant,
    fetchAvailableTenants,
    switchTenant,
    updateTenantSettings,
    updateTenantProfile,
    formatCurrency,
    formatDate,
    formatNumber,
    calculateItbis,
    getNextNCF,
    clearTenant,
    clearError
  }
}, {
  persist: true
})
